import { getAuth } from 'firebase/auth';
import { setDoc, getDoc, updateDoc, deleteDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { uploadBytes, deleteObject, ref as storageRef } from 'firebase/storage';
import { v4 as uuid } from 'uuid';
import { storage } from '../../firebase/config';
import { VideoThumbnailService } from './videoThumbnailService';
import { checkProjectSaveQuota, QuotaExceededError, ENFORCE_UPLOAD_SLOT_LIMIT } from './uploadQuotaPolicy';
import { normalizeCombos, userVideoStorageRef, userProjectsCollRef, userProjectDocRef } from '../utils';
import { normalizeVideoSettingsViewCrop } from '../types';
import { resolveVideoUploadType } from '../../utils/videoFileTypes';
import { formatMB } from '../../utils/videoValidation';
import type { Segment, VideoSettings, PracticeProject, UploadProjectSource, YouTubeProjectSource } from '../types';

/**
 * Storage rejects an upload the client already approved with a bare
 * `storage/unauthorized`, which reads as a permissions problem even when the real
 * cause is a rule the client disagrees with — a size cap in storage.rules lower
 * than the app's own limit produced exactly that, and looked like a .mov-specific
 * bug. Name the file's size and type so the next occurrence is diagnosable.
 */
const asUploadRejectionError = (error: unknown, sizeBytes: number, contentType: string): Error => {
  if ((error as { code?: string })?.code !== 'storage/unauthorized') {
    return error instanceof Error ? error : new Error(String(error));
  }

  console.error(
    `🔴 Storage rejected the upload (${formatMB(sizeBytes)} MB, ${contentType}). ` +
    'The deployed storage.rules may be out of date with the app — compare its size cap ' +
    'and allowed content types, then run `firebase deploy --only storage`.'
  );

  return new Error(
    `The server rejected this video (${formatMB(sizeBytes)} MB, ${contentType}). ` +
    'It may be larger than the upload limit, or in a format the server is not configured to accept.'
  );
};

/**
 * Save a new upload-backed practice project to users/{uid}/projects.
 * All new saves from the upload flow go here instead of users/{uid}/videos.
 */
export async function saveUploadProject(
  file: File,
  title: string,
  segments: Segment[],
  settings: VideoSettings
): Promise<{ id: string; project: Omit<PracticeProject, 'id'> }> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const uid = user.uid;

  const id = uuid();
  // Store the upload under its real extension/MIME type so .mov files stay
  // playable (Safari rejects QuickTime bytes labelled video/mp4).
  const { contentType, extension } = await resolveVideoUploadType(file);
  const videoRef = userVideoStorageRef(uid, id, extension);

  console.log('🟡 Starting project save...');

  // Size limit is always enforced; the slot-count cap follows the policy flag.
  const quotaResult = await checkProjectSaveQuota({
    sourceType: 'upload',
    uid,
    fileSizeBytes: file.size
  });
  const quotaBlockIsEnforced =
    quotaResult.reason === 'size_limit' ||
    quotaResult.reason === 'source_unavailable' ||
    (quotaResult.reason === 'slot_limit' && ENFORCE_UPLOAD_SLOT_LIMIT);

  if (!quotaResult.allowed && quotaBlockIsEnforced) {
    throw new QuotaExceededError(quotaResult.message!, quotaResult.reason!);
  }

  let thumbnailPath: string | undefined;
  try {
    thumbnailPath = await VideoThumbnailService.extractAndUploadThumbnail(file, uid, id, 0);
    console.log('🟢 Thumbnail created:', thumbnailPath);
  } catch (err) {
    console.warn('⚠️ Thumbnail creation failed, continuing without thumbnail:', err);
  }

  console.log('🟡 Uploading video to Storage...');
  try {
    await uploadBytes(videoRef, file, {
      cacheControl: 'public, max-age=31536000',
      contentType
    });
  } catch (error) {
    throw asUploadRejectionError(error, file.size, contentType);
  }
  console.log('🟢 Video uploaded');

  const source: UploadProjectSource = {
    type: 'upload',
    storagePath: videoRef.fullPath,
    uploadSize: file.size,
    ...(thumbnailPath ? { thumbnailPath } : {})
  };

  const normalizedSegments = normalizeCombos(segments);
  const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
  const projectData: any = {
    schemaVersion: 1,
    ownerId: uid,
    source,
    title: title || file.name,
    segments: normalizedSegments,
    settings: normalizedSettings,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  console.log('🟡 Saving project metadata to Firestore...');
  await setDoc(userProjectDocRef(uid, id), projectData);
  console.log('🟢 Project metadata saved');

  return { id, project: projectData };
}

/**
 * Save a YouTube-backed practice project to users/{uid}/projects.
 * No video is uploaded; the source carries only YouTube identity metadata.
 * Does not consume upload-backed storage quota.
 */
export async function saveYouTubeProject(
  source: YouTubeProjectSource,
  title: string,
  segments: Segment[],
  settings: VideoSettings
): Promise<{ id: string; project: Omit<PracticeProject, 'id'> }> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const uid = user.uid;

  const id = uuid();
  const cleanSource: YouTubeProjectSource = {
    type: 'youtube',
    videoId: source.videoId,
    canonicalUrl: source.canonicalUrl
  };

  if (source.startTime !== undefined) cleanSource.startTime = source.startTime;
  if (source.videoTitle !== undefined) cleanSource.videoTitle = source.videoTitle;
  if (source.thumbnailUrl !== undefined) cleanSource.thumbnailUrl = source.thumbnailUrl;

  const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
  const projectData: any = {
    schemaVersion: 1,
    ownerId: uid,
    source: cleanSource,
    title: title || cleanSource.videoTitle || cleanSource.canonicalUrl,
    segments: normalizeCombos(segments),
    settings: normalizedSettings,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(userProjectDocRef(uid, id), projectData);
  console.log('🟢 YouTube project metadata saved');

  return { id, project: projectData };
}

/**
 * List all practice projects for the current user from users/{uid}/projects.
 */
export async function listProjects(): Promise<PracticeProject[]> {
  const { currentUser } = getAuth();
  if (!currentUser) return [];
  const uid = currentUser.uid;

  const snap = await getDocs(userProjectsCollRef(uid));
  return snap.docs.map(d => {
    const data = d.data() as any;
    return {
      id: d.id,
      ...data,
      segments: normalizeCombos(data.segments || []),
      settings: normalizeVideoSettingsViewCrop(data.settings || {}) as VideoSettings
    };
  });
}

/**
 * Delete a practice project: removes Firestore doc, uploaded video, and thumbnail.
 */
export async function deleteProject(projectId: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const uid = user.uid;

  const docRef = userProjectDocRef(uid, projectId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data() as any;
    const source = data.source || {};

    // Only upload-backed projects have Firebase Storage artifacts to remove.
    if (source.type === 'upload') {
      const uploadSource = source as UploadProjectSource;
      if (uploadSource.storagePath) {
        try {
          const videoStorageRef = storageRef(storage, uploadSource.storagePath);
          await deleteObject(videoStorageRef);
          console.log('🟢 Project video deleted from Storage');
        } catch (err) {
          console.warn('⚠️ Failed to delete project video from Storage:', err);
        }
      }

      if (uploadSource.thumbnailPath) {
        try {
          await VideoThumbnailService.deleteThumbnail(uploadSource.thumbnailPath);
          console.log('🟢 Project thumbnail deleted');
        } catch (err) {
          console.warn('⚠️ Failed to delete project thumbnail:', err);
        }
      }
    }
  }

  await deleteDoc(docRef);
  console.log('🟢 Project metadata deleted from Firestore');
}

/**
 * Update segments and settings for an existing practice project.
 */
export async function updateProjectSegments(
  projectId: string,
  segments: Segment[],
  settings: VideoSettings
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const uid = user.uid;

  const docRef = userProjectDocRef(uid, projectId);
  const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
  await updateDoc(docRef, {
    segments: normalizeCombos(segments),
    settings: normalizedSettings,
    updatedAt: serverTimestamp()
  });
  console.log('🟢 Project segments updated');
}

/**
 * Rename a practice project.
 */
export async function renameProject(projectId: string, title: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const uid = user.uid;

  const docRef = userProjectDocRef(uid, projectId);
  await updateDoc(docRef, { title, updatedAt: serverTimestamp() });
  console.log('🟢 Project renamed');
}
